import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import * as AssessmentService from '@services/AssessmentService';
import { useAppTheme } from '@contexts/ThemeContext';
import { Plus, Trash2, X, ChevronDown } from 'lucide-react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import Spinner from './components/Spinner';
import './ModuleAssessmentMapping.css';

const ModuleAssessmentMapping: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State for dropdowns data
  const [assessments, setAssessments] = useState<AssessmentService.Assessment[]>([]);
  const [dimensions, setDimensions] = useState<AssessmentService.Dimension[]>([]);
  const [modules, setModules] = useState<AssessmentService.Module[]>([]);
  const [chartTypes, setChartTypes] = useState<AssessmentService.ChartType[]>([]);

  // State for selected values
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<number | null>(null);
  const [selectedAssessmentType, setSelectedAssessmentType] = useState<number | null>(null);
  const [selectedDimensionId, setSelectedDimensionId] = useState<number | string>('');
  const [selectedModuleId, setSelectedModuleId] = useState<number | string>('');

  // State for existing mappings
  const [mappings, setMappings] = useState<AssessmentService.ModuleAssessmentMapping[]>([]);

  // State for modals
  const [showDimensionModal, setShowDimensionModal] = useState(false);
  const [showModuleModal, setShowModuleModal] = useState(false);

  // State for creating new dimension
  const [newDimensionName, setNewDimensionName] = useState('');
  const [newDimensionChartId, setNewDimensionChartId] = useState<number | ''>('');

  // State for creating new module
  const [newModuleName, setNewModuleName] = useState('');

  // State for loading and errors
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [createMappingLoading, setCreateMappingLoading] = useState(false);
  const [createDimensionLoading, setCreateDimensionLoading] = useState(false);
  const [createModuleLoading, setCreateModuleLoading] = useState(false);
  const [deleteMappingLoading, setDeleteMappingLoading] = useState<number | null>(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Check for assessmentId in URL and pre-select it
  useEffect(() => {
    const assessmentIdParam = searchParams.get('assessmentId');
    if (assessmentIdParam && assessments.length > 0) {
      const assessmentId = Number(assessmentIdParam);
      if (!isNaN(assessmentId)) {
        setSelectedAssessmentId(assessmentId);

        // Also set the assessment type
        const selectedAssessment = assessments.find(a => a.assessmentId === assessmentId);
        setSelectedAssessmentType(selectedAssessment?.assessment_type_id || null);
      }
    }
  }, [searchParams, assessments]);

  useEffect(() => {
    if (selectedAssessmentId) {
      fetchMappings(selectedAssessmentId);
    }
  }, [selectedAssessmentId]);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all assessments including drafts
      const [assessmentsRes, draftRes, dimensionsRes, modulesRes, chartTypesRes] = await Promise.all([
        AssessmentService.getAssessments(),
        AssessmentService.getDraftAssessments(),
        AssessmentService.getDimensions(),
        AssessmentService.getModules(),
        AssessmentService.getChartTypes(),
      ]);

      // Combine published and draft assessments
      let allAssessments: AssessmentService.Assessment[] = [];
      if (assessmentsRes.success && assessmentsRes.data) {
        allAssessments = [...assessmentsRes.data];
      }
      if (draftRes.success && draftRes.data) {
        allAssessments = [...allAssessments, ...draftRes.data];
      }
      setAssessments(allAssessments);

      if (dimensionsRes.success && dimensionsRes.data) {
        console.log('Dimensions data:', dimensionsRes.data);
        setDimensions(dimensionsRes.data);
      } else {
        console.error('Failed to fetch dimensions:', dimensionsRes.error);
      }

      if (modulesRes.success && modulesRes.data) {
        console.log('Modules data:', modulesRes.data);
        setModules(modulesRes.data);
      } else {
        console.error('Failed to fetch modules:', modulesRes.error);
      }

      if (chartTypesRes.success && chartTypesRes.data) {
        console.log('Chart types data:', chartTypesRes.data);
        setChartTypes(chartTypesRes.data);
      } else {
        console.error('Failed to fetch chart types:', chartTypesRes.error);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async (assessmentId: number) => {
    try {
      const response = await AssessmentService.getModuleAssessmentMappings(assessmentId);
      if (response.success && response.data) {
        setMappings(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching mappings:', err);
    }
  };

  const handleCreateDimension = async () => {
    if (!newDimensionName.trim()) {
      setError('Dimension name is required');
      setTimeout(() => setError(null), 1000);
      return;
    }

    setCreateDimensionLoading(true);
    setError(null);
    setSuccessMessage(null);

    // Track start time to ensure minimum 1 second loading
    const startTime = Date.now();

    try {
      const dimensionData: AssessmentService.CreateDimensionData = {
        dimension_name: newDimensionName,
        ...(newDimensionChartId && { chart_id: Number(newDimensionChartId) }),
      };

      const response = await AssessmentService.createDimension(dimensionData);

      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time to ensure minimum 1 second loading
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      if (response.success && response.data) {
        setCreateDimensionLoading(false);
        setSuccessMessage('Dimension created successfully');
        setShowDimensionModal(false);
        setNewDimensionName('');
        setNewDimensionChartId('');

        // Refresh dimensions list
        const dimensionsRes = await AssessmentService.getDimensions();
        if (dimensionsRes.success && dimensionsRes.data) {
          setDimensions(dimensionsRes.data);
          setSelectedDimensionId(response.data.data.dimension_id);
        }

        // Auto-hide success message after 1 second
        setTimeout(() => setSuccessMessage(null), 1000);
      } else {
        throw new Error(response.error?.message || 'Failed to create dimension');
      }
    } catch (err: any) {
      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setCreateDimensionLoading(false);
      setError(err.message || 'Failed to create dimension');

      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
    }
  };

  const handleCreateModule = async () => {
    if (!newModuleName.trim()) {
      setError('Module name is required');
      setTimeout(() => setError(null), 1000);
      return;
    }

    setCreateModuleLoading(true);
    setError(null);
    setSuccessMessage(null);

    // Track start time to ensure minimum 1 second loading
    const startTime = Date.now();

    try {
      const moduleData: AssessmentService.CreateModuleData = {
        title: newModuleName,
      };

      const response = await AssessmentService.createModule(moduleData);

      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time to ensure minimum 1 second loading
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      if (response.success && response.data) {
        setCreateModuleLoading(false);
        setSuccessMessage('Module created successfully');
        setShowModuleModal(false);
        setNewModuleName('');

        // Refresh modules list
        const modulesRes = await AssessmentService.getModules();
        if (modulesRes.success && modulesRes.data) {
          setModules(modulesRes.data);
          setSelectedModuleId(response.data.data.moduleId);
        }

        // Auto-hide success message after 1 second
        setTimeout(() => setSuccessMessage(null), 1000);
      } else {
        throw new Error(response.error?.message || 'Failed to create module');
      }
    } catch (err: any) {
      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setCreateModuleLoading(false);
      setError(err.message || 'Failed to create module');

      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
    }
  };

  const handleCreateMapping = async () => {
    if (!selectedAssessmentId || !selectedDimensionId || !selectedModuleId) {
      setError('Please select assessment, dimension, and module');
      setTimeout(() => setError(null), 1000);
      return;
    }

    if (selectedDimensionId === 'new' || selectedModuleId === 'new') {
      setError('Please complete creating new dimension/module first');
      setTimeout(() => setError(null), 1000);
      return;
    }

    setCreateMappingLoading(true);
    setError(null);
    setSuccessMessage(null);

    // Track start time to ensure minimum 1 second loading
    const startTime = Date.now();

    try {
      const mappingData: AssessmentService.CreateMappingData = {
        assessment_id: selectedAssessmentId,
        dimension_id: Number(selectedDimensionId),
        module_id: Number(selectedModuleId),
      };

      const response = await AssessmentService.createModuleAssessmentMapping(mappingData);

      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time to ensure minimum 1 second loading
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      if (response.success) {
        setCreateMappingLoading(false);
        setSuccessMessage('Mapping created successfully');
        setSelectedDimensionId('');
        setSelectedModuleId('');

        // Refresh mappings
        if (selectedAssessmentId) {
          fetchMappings(selectedAssessmentId);
        }

        // Auto-hide success message after 1 second
        setTimeout(() => setSuccessMessage(null), 1000);
      } else {
        throw new Error(response.error?.message || 'Failed to create mapping');
      }
    } catch (err: any) {
      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setCreateMappingLoading(false);
      setError(err.message || 'Failed to create mapping');

      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
    }
  };

  const handleDeleteMapping = async (mapping: AssessmentService.ModuleAssessmentMapping, index: number) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return;
    }

    setDeleteMappingLoading(index);
    setError(null);
    setSuccessMessage(null);

    // Track start time to ensure minimum 1 second loading
    const startTime = Date.now();

    try {
      const response = await AssessmentService.deleteModuleAssessmentMapping(
        mapping.module_id,
        mapping.assessment_id,
        mapping.dimension_id
      );

      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time to ensure minimum 1 second loading
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      if (response.success) {
        setDeleteMappingLoading(null);
        setSuccessMessage('Mapping deleted successfully');

        // Refresh mappings
        if (selectedAssessmentId) {
          fetchMappings(selectedAssessmentId);
        }

        // Auto-hide success message after 1 second
        setTimeout(() => setSuccessMessage(null), 1000);
      } else {
        throw new Error(response.error?.message || 'Failed to delete mapping');
      }
    } catch (err: any) {
      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setDeleteMappingLoading(null);
      setError(err.message || 'Failed to delete mapping');

      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
    }
  };


  if (loading) {
    return (
      <Layout>
        <div className="module-mapping-container" data-theme={isDarkMode ? 'dark' : 'light'}>
          <Spinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="module-mapping-container" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Module-Assessment Mapping</h1>
            <p className="page-subtitle">Manage mappings between modules, dimensions, and assessments</p>
          </div>
           <button
            className="btn-primary"
            onClick={() => {
              if (selectedAssessmentId) {
                // Navigate to create-question page
                // The CreateQuestion component handles both regular and 360 feedback questions
                // based on assessmentTypeId (shows Create360Question when assessmentTypeId === 3)
                navigate(`/create-question?assessmentId=${selectedAssessmentId}`);
              } else {
                setError('Please select an assessment first');
              }
            }}
            disabled={!selectedAssessmentId}
            title={selectedAssessmentId ? "Go to Create Question page" : "Select an assessment first"}
          >
            <Plus size={20} />
            <span>Create Question</span>
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
            <button className="alert-close" onClick={() => setError(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            {successMessage}
            <button className="alert-close" onClick={() => setSuccessMessage(null)}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Assessment Selector */}
        <div className="form-section">
          <h2 className="section-title">Select Assessment</h2>
          <div className="form-group">
            <FormControl fullWidth>
              <InputLabel id="assessment-select-label">Select Assessment</InputLabel>
              <Select
                labelId="assessment-select-label"
                id="assessment-select"
                value={selectedAssessmentId || ''}
                label="Select Assessment"
                onChange={(e: SelectChangeEvent<number | ''>) => {
                  const value = e.target.value;
                  setSelectedAssessmentId(value === '' ? null : Number(value));

                  // Store assessment type when assessment is selected
                  if (value !== '') {
                    const selectedAssessment = assessments.find(a => a.assessmentId === Number(value));
                    setSelectedAssessmentType(selectedAssessment?.assessment_type_id || null);
                  } else {
                    setSelectedAssessmentType(null);
                  }
                }}
                IconComponent={(props) => <ChevronDown {...props} size={20} />}
              >
                <MenuItem value="">
                  <em>-- Select Assessment --</em>
                </MenuItem>
                {assessments.map((assessment) => (
                  <MenuItem key={assessment.assessmentId} value={assessment.assessmentId}>
                    {assessment.assessmentTitle}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {selectedAssessmentId && (
          <>
            {/* Create Mapping Form */}
            <div className="form-section">
              <h2 className="section-title">Create New Mapping</h2>

              <div className="form-row">
                <div className="form-group">
                  <div className="label-with-button">
                    <label className="form-label">Dimension</label>
                    <button
                      type="button"
                      className="btn-create-inline"
                      onClick={() => setShowDimensionModal(true)}
                      title="Create new dimension"
                    >
                      <Plus size={16} />
                      New
                    </button>
                  </div>
                  <FormControl fullWidth>
                    <InputLabel id="dimension-select-label">Select Dimension</InputLabel>
                    <Select
                      labelId="dimension-select-label"
                      id="dimension-select"
                      value={selectedDimensionId || ''}
                      label="Select Dimension"
                      onChange={(e: SelectChangeEvent<number | string>) => {
                        const value = e.target.value;
                        setSelectedDimensionId(value === '' ? '' : Number(value));
                      }}
                      IconComponent={(props) => <ChevronDown {...props} size={20} />}
                    >
                      <MenuItem value="">
                        <em>-- Select Dimension --</em>
                      </MenuItem>
                      {dimensions.map((dimension) => (
                        <MenuItem key={dimension.dimension_id} value={dimension.dimension_id}>
                          {dimension.dimension_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                <div className="form-group">
                  <div className="label-with-button">
                    <label className="form-label">Module</label>
                    <button
                      type="button"
                      className="btn-create-inline"
                      onClick={() => setShowModuleModal(true)}
                      title="Create new module"
                    >
                      <Plus size={16} />
                      New
                    </button>
                  </div>
                  <FormControl fullWidth>
                    <InputLabel id="module-select-label">Select Module</InputLabel>
                    <Select
                      labelId="module-select-label"
                      id="module-select"
                      value={selectedModuleId || ''}
                      label="Select Module"
                      onChange={(e: SelectChangeEvent<number | string>) => {
                        const value = e.target.value;
                        setSelectedModuleId(value === '' ? '' : Number(value));
                      }}
                      IconComponent={(props) => <ChevronDown {...props} size={20} />}
                    >
                      <MenuItem value="">
                        <em>-- Select Module --</em>
                      </MenuItem>
                      {modules.map((module) => (
                        <MenuItem key={module.moduleId} value={module.moduleId}>
                          {module.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>

              <button
                className="btn-primary"
                onClick={handleCreateMapping}
                disabled={!selectedDimensionId || !selectedModuleId || createMappingLoading}
              >
                {createMappingLoading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    <span>Add Mapping</span>
                  </>
                )}
              </button>
            </div>

            {/* Existing Mappings Table */}
            <div className="form-section">
              <h2 className="section-title">Existing Mappings</h2>

              {mappings.length === 0 ? (
                <div className="empty-state">No mappings found for this assessment</div>
              ) : (
                <div className="table-container">
                  <table className="mappings-table">
                    <thead>
                      <tr>
                        <th>Dimension</th>
                        <th>Module</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((mapping, index) => (
                        <tr key={`${mapping.module_id}-${mapping.assessment_id}-${mapping.dimension_id}-${index}`}>
                          <td>{mapping.dimension_name || mapping.dimension_id}</td>
                          <td>{mapping.module_name || mapping.module_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Create Dimension Modal */}
        {showDimensionModal && (
          <div className="modal-overlay" onClick={() => setShowDimensionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create New Dimension</h3>
                <button className="modal-close" onClick={() => setShowDimensionModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Dimension Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newDimensionName}
                    onChange={(e) => setNewDimensionName(e.target.value)}
                    placeholder="Enter dimension name"
                  />
                </div>
                <div className="form-group">
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="chart-type-label">Chart Type (Optional)</InputLabel>
                    <Select
                      labelId="chart-type-label"
                      id="chart-type-select"
                      value={newDimensionChartId || ''}
                      label="Chart Type (Optional)"
                      onChange={(e: SelectChangeEvent<number | ''>) => {
                        const value = e.target.value;
                        setNewDimensionChartId(value === '' ? '' : Number(value));
                      }}
                    >
                      <MenuItem value="">
                        <em>-- No Chart Type --</em>
                      </MenuItem>
                      {chartTypes.map((chart) => (
                        <MenuItem key={chart.chart_id} value={chart.chart_id}>
                          {chart.chart_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowDimensionModal(false)}
                  disabled={createDimensionLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateDimension}
                  disabled={createDimensionLoading}
                >
                  {createDimensionLoading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Creating...</span>
                    </>
                  ) : (
                    'Create Dimension'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Module Modal */}
        {showModuleModal && (
          <div className="modal-overlay" onClick={() => setShowModuleModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create New Module</h3>
                <button className="modal-close" onClick={() => setShowModuleModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Module Title *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newModuleName}
                    onChange={(e) => setNewModuleName(e.target.value)}
                    placeholder="Enter module title"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => setShowModuleModal(false)}
                  disabled={createModuleLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateModule}
                  disabled={createModuleLoading}
                >
                  {createModuleLoading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Creating...</span>
                    </>
                  ) : (
                    'Create Module'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ModuleAssessmentMapping;
